import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Truck, Car, Wrench, ClipboardCheck, Phone, Cog, ArrowRight } from "lucide-react";
import { PublicNav, Footer } from "@/components/PublicLayout";

const services = [
  { key: "commercial", icon: Truck, title: "Commercial & Heavy-Duty Maintenance", desc: "Keep your semis, box trucks and fleets DOT-ready with scheduled PM programs.",
    img: "https://images.unsplash.com/photo-1574757974346-45bae947d89a?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200" },
  { key: "passenger", icon: Car, title: "Passenger Vehicles", desc: "Honest diagnostics and repairs for your daily driver.",
    img: "https://images.unsplash.com/photo-1615906655593-ad0386982a0f?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200" },
  { key: "custom", icon: Cog, title: "Custom Builds", desc: "Boats, ATVs, UTVs and lawn equipment — service and custom work for everything else.",
    img: "https://static.prod-images.emergentagent.com/jobs/1a9ea1bb-2c9c-4efc-8540-f074ca17f8fb/images/af6328a2144df1d165c2d330ddc649efc633a40e2eb4453cbd73eaa2995a69de.jpeg" },
];

export default function Home() {
  return (
    <div className="bg-[#09090b] min-h-screen">
      <PublicNav />

      <section className="relative overflow-hidden grain">
        <div className="absolute inset-0 bg-cover bg-center"
          style={{ backgroundImage: "url('https://images.unsplash.com/photo-1760944016312-93ac6f9402fe?crop=entropy&cs=srgb&fm=jpg&q=85&w=1920')" }} />
        <div className="absolute inset-0 bg-black/70" />
        <div className="relative z-10 max-w-7xl mx-auto px-6 py-32 md:py-44">
          <motion.p initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}
            className="text-xs font-bold tracking-[0.3em] uppercase text-yellow-500 mb-4">Oklahoma City · 405-219-7625</motion.p>
          <motion.h1 initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }}
            className="font-display text-4xl sm:text-5xl lg:text-7xl font-black tracking-tighter uppercase max-w-4xl leading-[0.95]">
            Maintenance that keeps you <span className="text-yellow-500">moving.</span>
          </motion.h1>
          <motion.p initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }}
            className="text-zinc-300 text-lg max-w-2xl mt-6">
            From heavy-duty fleets to daily drivers and custom rigs — J&amp;D Truck Repair delivers transparent estimates you approve online, powered by QuickBooks billing.
          </motion.p>
          <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.3 }} className="flex flex-wrap gap-4 mt-10">
            <Link to="/request-quote" data-testid="hero-request-quote"
              className="bg-yellow-500 text-black font-bold uppercase px-8 py-4 flex items-center gap-2 hover:bg-yellow-300 transition-colors duration-150">
              Request a Quote <ArrowRight size={18} />
            </Link>
            <Link to="/portal" data-testid="hero-customer-portal"
              className="border border-zinc-600 text-white font-bold uppercase px-8 py-4 hover:border-yellow-500 hover:text-yellow-500 transition-colors duration-150">
              Approve My Estimate
            </Link>
          </motion.div>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-6 py-24">
        <p className="text-xs font-bold tracking-[0.2em] uppercase text-zinc-500 mb-2">What we service</p>
        <h2 className="font-display text-3xl lg:text-4xl font-bold uppercase tracking-tight mb-12">Built for every rig on the road</h2>
        <div className="grid md:grid-cols-3 gap-6">
          {services.map((s, i) => (
            <motion.div key={s.key} initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }} transition={{ delay: i * 0.1 }}>
              <Link to={`/services/${s.key}`} data-testid={`service-card-${s.key}`}
                className="group block border border-zinc-800 bg-zinc-950 hover:border-yellow-500 transition-colors duration-150">
                <div className="h-52 bg-cover bg-center relative" style={{ backgroundImage: `url('${s.img}')` }}>
                  <div className="absolute inset-0 bg-black/40 group-hover:bg-black/20 transition-colors duration-150" />
                  <s.icon className="absolute bottom-4 left-4 text-yellow-500" size={30} />
                </div>
                <div className="p-6">
                  <h3 className="font-display text-xl font-bold mb-2">{s.title}</h3>
                  <p className="text-zinc-400 text-sm mb-4">{s.desc}</p>
                  <span className="text-yellow-500 text-sm font-bold uppercase flex items-center gap-1">Learn more <ArrowRight size={14} /></span>
                </div>
              </Link>
            </motion.div>
          ))}
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-6 pb-24">
        <div className="grid md:grid-cols-3 gap-6">
          {[
            { icon: ClipboardCheck, t: "Approve online", d: "Review every part & labor line and approve exactly what you want done." },
      { icon: Wrench, t: "Multi-vehicle accounts", d: "Manage your whole fleet under one customer account." },
            { icon: Phone, t: "QuickBooks billing", d: "Invoices sync to QuickBooks for fast, clean payments." },
          ].map((f) => (
            <div key={f.t} className="border border-zinc-800 bg-zinc-950 p-8">
              <f.icon className="text-yellow-500 mb-4" size={28} />
              <h4 className="font-display text-lg font-bold mb-2">{f.t}</h4>
              <p className="text-zinc-400 text-sm">{f.d}</p>
            </div>
          ))}
        </div>
      </section>

      <Footer />
    </div>
  );
}
